import { CheckIcon, XIcon } from "lucide-react";

import { Column, LoadingIndicator, Row, Text } from "@/components";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

import { useDecideJobPauseRequest } from "@/hooks/mutations/useDecideJobPauseRequest";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { getJobLink } from "@/lib/notificationLinks";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { cn } from "@/lib/utils";
import { JobPauseType, PendingPauseRequest } from "@/types";

// One open pause/cover request, awaiting a yes or no. It has no dismiss of its
// own — deciding is the only way it leaves the stack, which is why the request
// is polled rather than delivered as a notification.
export const PauseRequestCard = ({
  pauseRequest,
}: {
  pauseRequest: PendingPauseRequest;
}) => {
  /* Database CRUD Operations */
  // Update
  const { mutate: decideJobPauseRequest, isPending } =
    useDecideJobPauseRequest();

  /* Handlers */
  const handleDecide = (approved: boolean) => {
    mixpanelUtils.trackButtonClick(
      approved ? "Pause request approved" : "Pause request denied",
      MIXPANEL_SCREENS.DASHBOARD,
      { pause_type: pauseRequest.pauseType },
    );
    decideJobPauseRequest({
      approved,
      requestId: pauseRequest.requestId,
    });
  };

  const handleOpenJob = () => {
    mixpanelUtils.trackRowClick("Pause request", MIXPANEL_SCREENS.DASHBOARD, {
      pause_type: pauseRequest.pauseType,
    });
    window.open(
      getJobLink({
        companyId: pauseRequest.companyId,
        jobId: pauseRequest.jobId,
      }),
      "_blank",
    );
  };

  /* Render */
  const renderDecideButton = (approved: boolean) => {
    const Icon = approved ? CheckIcon : XIcon;
    return (
      <Button
        className="h-8 w-8"
        disabled={isPending}
        onClick={(e) => {
          e.stopPropagation();
          handleDecide(approved);
        }}
        size="icon"
        variant="ghost"
      >
        <Icon
          className={cn(
            "h-4 w-4",
            approved ? "text-emerald-600" : "text-rose-600",
          )}
        />
      </Button>
    );
  };

  const renderActionRail = () => {
    if (isPending) {
      return (
        <Column className="shrink-0 items-center justify-center">
          <LoadingIndicator variant="small" />
        </Column>
      );
    }
    return (
      <Column
        className="shrink-0 items-center justify-around"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        {renderDecideButton(true)}
        {renderDecideButton(false)}
      </Column>
    );
  };

  const renderDetail = (label: string, value: string) => (
    <Row className="min-w-0 gap-1">
      <Text className="shrink-0 text-xs font-semibold text-slate-500">
        {label}
      </Text>
      <Text className="min-w-0 text-xs font-normal text-slate-500">
        {value}
      </Text>
    </Row>
  );

  const render = () => (
    <Card
      className="pointer-events-auto w-full cursor-pointer overflow-hidden rounded-xl border-slate-200 bg-white p-0 shadow-lg transition-shadow hover:shadow-xl"
      onClick={handleOpenJob}
    >
      <Row className="items-stretch gap-3 p-4">
        {renderActionRail()}
        <Column className="min-w-0 flex-1 gap-1">
          <Text className="text-sm font-semibold text-slate-900">
            {pauseRequest.pauseType === JobPauseType.COVERED
              ? "Cover"
              : "Pause"}{" "}
            Request - {pauseRequest.jobTitle} at {pauseRequest.companyName}
          </Text>
          {renderDetail("Requested by:", pauseRequest.stafferName)}
          {renderDetail("Reason:", pauseRequest.pauseReason)}
          {renderDetail("Pipeline State:", pauseRequest.pipelineState)}
        </Column>
      </Row>
    </Card>
  );

  return render();
};
